import { useCallback, useEffect, useEffectEvent, useState } from "react";
import { ApiError } from "./api";

/**
 * Load something from the API, with loading and error state. `key` says when
 * to load again (a search term, say); `reload()` loads again after a change.
 * The previous data stays on screen while a reload is in flight.
 */
export function useApi<T>(load: () => Promise<T>, key = "") {
  const [version, setVersion] = useState(0);
  const [state, setState] = useState<{ data: T | null; error: string | null; loading: boolean }>({ data: null, error: null, loading: true });
  const fetchNow = useEffectEvent(() => load());

  useEffect(() => {
    let current = true;
    fetchNow().then(
      (data) => current && setState({ data, error: null, loading: false }),
      (e: unknown) => current && setState({ data: null, error: describe(e), loading: false }),
    );
    return () => {
      current = false;
    };
  }, [key, version]);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  return { ...state, reload };
}

/** A failure in words a staff member can act on. */
export function describe(e: unknown): string {
  if (e instanceof ApiError) {
    // Listing rules speak to the owner; say what staff can do about them.
    if (e.code === "not_verified" && e.status === 403) return "The owner is not verified yet. Approve them in Verifications, then publish.";
    if (e.code === "overdue_requests") return "The owner has overdue requests on this listing. They must answer them before it goes live again.";
    if (e.status === 403) return "This account is not staff, or the 6-digit code has not been entered.";
    return e.message;
  }
  return e instanceof Error ? e.message : "Something went wrong.";
}
